import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  ActionIcon, Button, Group, Menu, Modal, Select, Stack, Text, TextInput, Textarea, Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { modals } from '@mantine/modals';
import {
  IconDots, IconEdit, IconPlus, IconTrash,
} from '@tabler/icons-react';
import {
  MantineReactTable, useMantineReactTable, type MRT_ColumnDef, type MRT_PaginationState,
} from 'mantine-react-table';
import { useCrudResource } from '@/shared/hooks/useCrudResource';
import { EmptyState } from '@/shared/components/EmptyState';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

export const Route = createFileRoute('/_app/admin/categories')({ component: AdminCategoriesPage });

interface CategoryRow extends Denormalized {
  type: 'categories';
  id: string;
  name?: string;
  code?: string;
  description?: string;
  /** BE round 2: parent come relationship denormalizzata. */
  parent?: { id: string; type?: string; name?: string } | null;
}

interface CategoryFormValues {
  name: string;
  code: string;
  description: string;
  /** Id del parent selezionato dalla Select (vuoto = root). */
  parentCategory: string;
}

const INITIAL: CategoryFormValues = { name: '', code: '', description: '', parentCategory: '' };

function AdminCategoriesPage() {
  const [pagination, setPagination] = useState<MRT_PaginationState>({ pageIndex: 0, pageSize: 25 });
  const [modalOpened, modal] = useDisclosure(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const form = useForm<CategoryFormValues>({
    mode: 'controlled',
    initialValues: INITIAL,
    validate: {
      name: (v) => (v.trim() ? null : 'Nome obbligatorio'),
    },
  });

  const crud = useCrudResource<CategoryRow>({
    resource: 'categories',
    list: { page: { number: pagination.pageIndex + 1, size: pagination.pageSize } },
    labels: { create: 'Categoria creata', update: 'Categoria aggiornata', remove: 'Categoria eliminata' },
    onCreateSuccess: () => { modal.close(); form.reset(); },
    onUpdateSuccess: () => { modal.close(); form.reset(); },
  });
  const allCategories = crud.items;
  const isLoading = crud.isLoading;
  const isError = crud.query.isError;
  const parentOptions = useMemo(
    () => allCategories
      .filter((c) => c.id && c.name)
      .map((c) => ({ value: c.id, label: c.name ?? c.id })),
    [allCategories],
  );

  const openCreate = () => {
    setEditingId(null);
    form.setValues(INITIAL);
    modal.open();
  };

  const openEdit = (row: CategoryRow) => {
    setEditingId(row.id);
    form.setValues({
      name: row.name ?? '',
      code: row.code ?? '',
      description: row.description ?? '',
      parentCategory: row.parent?.id ?? '',
    });
    modal.open();
  };

  const onSubmit = form.onSubmit((values) => {
    const attributes = {
      name: values.name.trim(),
      code: values.code.trim() || null,
      description: values.description.trim() || null,
    };
    const relationships = {
      parent: { data: values.parentCategory ? { type: 'categories', id: values.parentCategory } : null },
    };
    if (editingId) crud.update.mutate({ id: editingId, attributes, relationships });
    else crud.create.mutate({ attributes, relationships });
  });

  const onDelete = (id: string) => {
    modals.openConfirmModal({
      title: 'Elimina categoria',
      children: <Text size="sm">Eliminare la categoria?</Text>,
      labels: { confirm: 'Elimina', cancel: 'Annulla' },
      confirmProps: { color: 'red' },
      onConfirm: () => crud.remove.mutate(id),
    });
  };

  const columns = useMemo<MRT_ColumnDef<CategoryRow>[]>(
    () => [
      { accessorKey: 'name', header: 'Nome' },
      { accessorKey: 'code', header: 'Slug / Code', size: 160 },
      { accessorKey: 'description', header: 'Descrizione' },
      {
        id: 'parent',
        header: 'Parent',
        size: 200,
        Cell: ({ row }) => {
          const parentId = row.original.parent?.id;
          const p = parentId ? allCategories.find((c) => c.id === parentId) : null;
          return <Text size="sm">{p?.name ?? (parentId ? <Text c="dimmed" size="xs">{parentId}</Text> : '—')}</Text>;
        },
      },
      {
        id: 'actions',
        header: '',
        size: 60,
        Cell: ({ row }) => (
          <Menu withinPortal position="bottom-end">
            <Menu.Target>
              <ActionIcon variant="subtle" aria-label="Azioni" onClick={(e) => e.stopPropagation()}>
                <IconDots size={16} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item leftSection={<IconEdit size={14} />} onClick={(e) => { e.stopPropagation(); openEdit(row.original); }}>
                Modifica
              </Menu.Item>
              <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={(e) => { e.stopPropagation(); void onDelete(row.original.id); }}>
                Elimina
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        ),
      },
    ],
    [allCategories],
  );

  const table = useMantineReactTable({
    columns,
    data: allCategories,
    rowCount: crud.total,
    state: {
      pagination,
      isLoading,
      showAlertBanner: isError,
    },
    manualPagination: true,
    enableSorting: false,
    enableColumnFilters: false,
    onPaginationChange: setPagination,
    initialState: { density: 'xs' },
    paginationDisplayMode: 'pages',
    enableStickyHeader: true,
    mantineTableBodyRowProps: ({ row }) => ({
      onClick: () => openEdit(row.original),
      style: { cursor: 'pointer' },
    }),
    mantineToolbarAlertBannerProps: isError
      ? { color: 'red', children: 'Errore caricamento' }
      : undefined,
    renderEmptyRowsFallback: () => (
      <EmptyState
        title="Nessuna categoria"
        description="Crea la prima categoria con il pulsante in alto."
        py="lg"
      />
    ),
  });

  return (
    <Stack>
      <Group justify="space-between" align="flex-start">
        <div>
          <Title order={1}>Categorie</Title>
          <Text c="dimmed" size="sm">Gestione delle categorie del tenant (struttura ad albero).</Text>
        </div>
        <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
          Nuova categoria
        </Button>
      </Group>
      <MantineReactTable table={table} />

      <Modal
        opened={modalOpened}
        onClose={() => { modal.close(); form.reset(); }}
        title={editingId ? 'Modifica categoria' : 'Nuova categoria'}
        centered
        size="md"
      >
        <form onSubmit={onSubmit}>
          <Stack>
            <TextInput
              label="Nome"
              required
              {...form.getInputProps('name')}
            />
            <TextInput
              label="Slug / Code"
              placeholder="es. fattura-pa"
              {...form.getInputProps('code')}
            />
            <Textarea
              label="Descrizione"
              autosize
              minRows={2}
              {...form.getInputProps('description')}
            />
            <Select
              label="Parent"
              placeholder="Nessuno"
              clearable
              searchable
              data={parentOptions.filter((o) => o.value !== editingId)}
              {...form.getInputProps('parentCategory')}
            />
            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => { modal.close(); form.reset(); }}>
                Annulla
              </Button>
              <Button type="submit" loading={crud.create.isPending || crud.update.isPending}>
                {editingId ? 'Salva' : 'Crea'}
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  );
}
